import { createReducer, createActions } from 'reduxsauce';
import Immutable from 'seamless-immutable';

export const INITIAL_STATE = Immutable({
  isLoading: false,
  errorMessage: null,
  successMessage: null,
  userId: null,
  hubs: [],
  groups: [],
  devices: [],
  rooms: [],
  logs: [],
  ajaxAccessLevelLinks: [],
  isAjaxIncludedInMonitoring: false,
});

const { Types, Creators } = createActions({
  loginAjaxRequest: ['customerId'],
  loginAjaxRequestSuccess: ['userId'],
  loginAjaxRequestFailure: ['errorMessage'],

  getHubsRequest: ['customerId', 'userId'],
  getHubsRequestSuccess: ['hubs'],
  getHubsRequestFailure: ['errorMessage'],

  getHubDetailsRequest: ['customerId', 'userId', 'hubId'],
  getHubDetailsRequestSuccess: ['hubDetails'],
  getHubDetailsRequestFailure: ['errorMessage'],

  resetAjax: null,
  resetHubDetailsRequest: null,
  resetAjaxAccessLevelLinks: null,

  getGroupsRequest: ['customerId', 'userId', 'hubId', 'hubIndex'],
  getGroupsRequestSuccess: ['singleHubGroups', 'hubId', 'hubIndex'],
  getGroupsRequestFailure: ['errorMessage'],

  getDevicesRequest: ['customerId', 'userId', 'hubId'],
  getDevicesRequestSuccess: ['devices'],
  getDevicesRequestFailure: ['errorMessage'],

  getRoomsRequest: ['customerId', 'userId', 'hubId'],
  getRoomsRequestSuccess: ['rooms'],
  getRoomsRequestFailure: ['errorMessage'],

  getLogsRequest: ['customerId', 'userId', 'hubId', 'pageNumber'],
  getLogsRequestSuccess: ['singleHubLogs', 'hubId'],
  getLogsRequestFailure: ['errorMessage'],

  armHubRequest: ['customerId', 'userId', 'hubId', 'command', 'logsPageNumber'],
  armHubRequestSuccess: ['hubDetails', 'singleHubLogs', 'hubId'],
  armHubRequestFailure: ['errorMessage'],

  armGroupRequest: ['customerId', 'userId', 'hubId', 'hubIndex', 'groupId', 'command', 'logsPageNumber'],
  armGroupRequestSuccess: ['singleHubGroups', 'hubDetails', 'singleHubLogs', 'hubId', 'hubIndex'],
  armGroupRequestFailure: ['errorMessage'],

  getAjaxAccessLevelLinksRequest: ['accessLevelId'],
  getAjaxAccessLevelLinksRequestSuccess: ['ajaxAccessLevelLinks'],
  getAjaxAccessLevelLinksRequestFailure: ['errorMessage'],

  updateAjaxAccessLevelLinksRequest: ['ajaxAccessLevelLinks', 'accessLevelId'],
  updateAjaxAccessLevelLinksRequestSuccess: ['ajaxAccessLevelLinks', 'successMessage'],
  updateAjaxAccessLevelLinksRequestFailure: ['errorMessage'],

  deleteAjaxAccessLevelLinkRequest: ['ajaxAccessLevelLinkId', 'accessLevelId'],
  deleteAjaxAccessLevelLinkRequestSuccess: ['ajaxAccessLevelLinks', 'successMessage'],
  deleteAjaxAccessLevelLinkRequestFailure: ['errorMessage'],

  setAjaxIncludedInMonitoring: ['isAjaxIncludedInMonitoring'],
});

export const loginAjaxRequest = (state) => state.merge({ isLoading: true, errorMessage: null });
export const loginAjaxRequestSuccess = (state, { userId }) => state.merge({ isLoading: false, userId });
export const loginAjaxRequestFailure = (state, { errorMessage }) => state.merge({ isLoading: false, errorMessage });

export const getHubsRequest = (state) => state.merge({ isLoading: true, errorMessage: null });
export const getHubsRequestSuccess = (state, { hubs }) => {
  const hubsWithIndex = hubs.map((hub, index) => ({ ...hub, hubIndex: index }));
  return state.merge({ isLoading: false, hubs: hubsWithIndex });
};
export const getHubsRequestFailure = (state, { errorMessage }) => state.merge({ isLoading: false, errorMessage });

export const getHubDetailsRequest = (state) => state.merge({ isLoading: true, errorMessage: null });
export const getHubDetailsRequestSuccess = (state, { hubDetails }) => {
  const hubToUpdateIndex = state.hubs.findIndex((hub) => hub.hubId === hubDetails.id);
  const updatedHubs = state.hubs.map((hub, index) => {
    if (index !== hubToUpdateIndex) {
      return hub;
    }
    return {
      ...hub,
      ...hubDetails,
      hasDetails: true,
    };
  });
  return state.merge({ isLoading: false, hubs: updatedHubs });
};
export const getHubDetailsRequestFailure = (state, { errorMessage }) => state.merge({ isLoading: false, errorMessage });

export const resetAjax = (state) => {
  return state.merge(INITIAL_STATE);
};
export const resetAjaxAccessLevelLinks = (state) => {
  return state.merge({ ajaxAccessLevelLinks: [] });
};
export const resetHubDetailsRequest = (state) => {
  const hubsWithoutHasDetailsFlag = state.hubs.map((hub) => ({ ...hub, hasDetails: false }));
  return state.merge({ hubs: hubsWithoutHasDetailsFlag });
};

export const getGroupsRequest = (state) => state.merge({ isLoading: true, errorMessage: null });
export const getGroupsRequestSuccess = (state, { singleHubGroups, hubId, hubIndex }) => {
  const newGroups = singleHubGroups.map((group) => ({ ...group, hubId, hubIndex }));
  const otherGroups = state.groups.filter((group) => group.hubId !== hubId);
  const updatedGroups = Immutable.asMutable(otherGroups)
    .concat(newGroups)
    .sort((a, b) => a.hubIndex - b.hubIndex);
  return state.merge({ isLoading: false, groups: updatedGroups });
};
export const getGroupsRequestFailure = (state, { errorMessage }) => state.merge({ isLoading: false, errorMessage });

export const getDevicesRequest = (state) => state.merge({ isLoading: true, errorMessage: null });
export const getDevicesRequestSuccess = (state, { devices }) => state.merge({ isLoading: false, devices });
export const getDevicesRequestFailure = (state, { errorMessage }) => state.merge({ isLoading: false, errorMessage });

export const getRoomsRequest = (state) => state.merge({ isLoading: true, errorMessage: null });
export const getRoomsRequestSuccess = (state, { rooms }) => state.merge({ isLoading: false, rooms });
export const getRoomsRequestFailure = (state, { errorMessage }) => state.merge({ isLoading: false, errorMessage });

export const getLogsRequest = (state) => state.merge({ isLoading: true, errorMessage: null });
export const getLogsRequestSuccess = (state, { singleHubLogs, hubId }) => {
  const previousLogs = state.logs;
  const otherHubsLogs = previousLogs.filter((entry) => entry.hubId !== hubId);
  const updatedLogs = otherHubsLogs.concat(singleHubLogs);
  return state.merge({ isLoading: false, logs: updatedLogs });
};
export const getLogsRequestFailure = (state, { errorMessage }) => state.merge({ isLoading: false, errorMessage });

export const armHubRequest = (state) => state.merge({ isLoading: true, errorMessage: null });
export const armHubRequestSuccess = (state, { hubDetails, singleHubLogs, hubId }) => {
  const hubToUpdateIndex = state.hubs.findIndex((hub) => hub.hubId === hubDetails.id);
  const updatedHubs = state.hubs.map((hub, index) => {
    if (index !== hubToUpdateIndex) {
      return hub;
    }
    return {
      ...hub,
      ...hubDetails,
    };
  });
  const previousLogs = state.logs;
  const otherHubsLogs = previousLogs.filter((entry) => entry.hubId !== hubId);
  const updatedLogs = otherHubsLogs.concat(singleHubLogs);
  return state.merge({ isLoading: false, hubs: updatedHubs, logs: updatedLogs });
};
export const armHubRequestFailure = (state, { errorMessage }) => state.merge({ isLoading: false, errorMessage });

export const armGroupRequest = (state) => state.merge({ isLoading: true, errorMessage: null });
export const armGroupRequestSuccess = (state, { singleHubGroups, hubDetails, singleHubLogs, hubId, hubIndex }) => {
  // update groups
  const newGroups = singleHubGroups.map((group) => ({ ...group, hubId, hubIndex }));
  const otherGroups = state.groups.filter((group) => group.hubId !== hubId);
  const updatedGroups = Immutable.asMutable(otherGroups)
    .concat(newGroups)
    .sort((a, b) => a.hubIndex - b.hubIndex);
  // update details
  const hubToUpdateIndex = state.hubs.findIndex((hub) => hub.hubId === hubDetails.id);
  const updatedHubs = state.hubs.map((hub, index) => {
    if (index !== hubToUpdateIndex) {
      return hub;
    }
    return {
      ...hub,
      ...hubDetails,
      hasDetails: true,
    };
  });
  // update logs
  const previousLogs = state.logs;
  const otherHubsLogs = previousLogs.filter((entry) => entry.hubId !== hubId);
  const updatedLogs = otherHubsLogs.concat(singleHubLogs);
  return state.merge({ isLoading: false, groups: updatedGroups, hubs: updatedHubs, logs: updatedLogs });
};
export const armGroupRequestFailure = (state, { errorMessage }) => state.merge({ isLoading: false, errorMessage });

const getAjaxAccessLevelLinksRequest = (state) =>
  state.merge({ isLoading: true, errorMessage: null, successMessage: null });
const getAjaxAccessLevelLinksRequestSuccess = (state, { ajaxAccessLevelLinks }) =>
  state.merge({
    isLoading: false,
    ajaxAccessLevelLinks,
  });
const getAjaxAccessLevelLinksRequestFailure = (state, { errorMessage }) =>
  state.merge({
    isLoading: false,
    errorMessage,
  });

const updateAjaxAccessLevelLinksRequest = (state) =>
  state.merge({ isLoading: true, errorMessage: null, successMessage: null });
const updateAjaxAccessLevelLinksRequestSuccess = (state, { ajaxAccessLevelLinks, successMessage }) =>
  state.merge({
    isLoading: false,
    ajaxAccessLevelLinks,
    successMessage,
  });
const updateAjaxAccessLevelLinksRequestFailure = (state, { errorMessage }) =>
  state.merge({
    isLoading: false,
    errorMessage,
  });

const deleteAjaxAccessLevelLinkRequest = (state) =>
  state.merge({ isLoading: true, errorMessage: null, successMessage: null });
const deleteAjaxAccessLevelLinkRequestSuccess = (state, { ajaxAccessLevelLinks, successMessage }) =>
  state.merge({
    isLoading: false,
    ajaxAccessLevelLinks,
    successMessage,
  });
const deleteAjaxAccessLevelLinkRequestFailure = (state, { errorMessage }) =>
  state.merge({
    isLoading: false,
    errorMessage,
  });
const setAjaxIncludedInMonitoring = (state, { isAjaxIncludedInMonitoring }) =>
  state.merge({ isAjaxIncludedInMonitoring });

export const reducer = createReducer(INITIAL_STATE, {
  [Types.LOGIN_AJAX_REQUEST]: loginAjaxRequest,
  [Types.LOGIN_AJAX_REQUEST_SUCCESS]: loginAjaxRequestSuccess,
  [Types.LOGIN_AJAX_REQUEST_FAILURE]: loginAjaxRequestFailure,

  [Types.GET_HUBS_REQUEST]: getHubsRequest,
  [Types.GET_HUBS_REQUEST_SUCCESS]: getHubsRequestSuccess,
  [Types.GET_HUBS_REQUEST_FAILURE]: getHubsRequestFailure,

  [Types.GET_HUB_DETAILS_REQUEST]: getHubDetailsRequest,
  [Types.GET_HUB_DETAILS_REQUEST_SUCCESS]: getHubDetailsRequestSuccess,
  [Types.GET_HUB_DETAILS_REQUEST_FAILURE]: getHubDetailsRequestFailure,

  [Types.RESET_AJAX]: resetAjax,
  [Types.RESET_AJAX_ACCESS_LEVEL_LINKS]: resetAjaxAccessLevelLinks,
  [Types.RESET_HUB_DETAILS_REQUEST]: resetHubDetailsRequest,

  [Types.GET_GROUPS_REQUEST]: getGroupsRequest,
  [Types.GET_GROUPS_REQUEST_SUCCESS]: getGroupsRequestSuccess,
  [Types.GET_GROUPS_REQUEST_FAILURE]: getGroupsRequestFailure,

  [Types.GET_DEVICES_REQUEST]: getDevicesRequest,
  [Types.GET_DEVICES_REQUEST_SUCCESS]: getDevicesRequestSuccess,
  [Types.GET_DEVICES_REQUEST_FAILURE]: getDevicesRequestFailure,

  [Types.GET_ROOMS_REQUEST]: getRoomsRequest,
  [Types.GET_ROOMS_REQUEST_SUCCESS]: getRoomsRequestSuccess,
  [Types.GET_ROOMS_REQUEST_FAILURE]: getRoomsRequestFailure,

  [Types.GET_LOGS_REQUEST]: getLogsRequest,
  [Types.GET_LOGS_REQUEST_SUCCESS]: getLogsRequestSuccess,
  [Types.GET_LOGS_REQUEST_FAILURE]: getLogsRequestFailure,

  [Types.ARM_HUB_REQUEST]: armHubRequest,
  [Types.ARM_HUB_REQUEST_SUCCESS]: armHubRequestSuccess,
  [Types.ARM_HUB_REQUEST_FAILURE]: armHubRequestFailure,

  [Types.ARM_GROUP_REQUEST]: armGroupRequest,
  [Types.ARM_GROUP_REQUEST_SUCCESS]: armGroupRequestSuccess,
  [Types.ARM_GROUP_REQUEST_FAILURE]: armGroupRequestFailure,

  [Types.GET_AJAX_ACCESS_LEVEL_LINKS_REQUEST]: getAjaxAccessLevelLinksRequest,
  [Types.GET_AJAX_ACCESS_LEVEL_LINKS_REQUEST_SUCCESS]: getAjaxAccessLevelLinksRequestSuccess,
  [Types.GET_AJAX_ACCESS_LEVEL_LINKS_REQUEST_FAILURE]: getAjaxAccessLevelLinksRequestFailure,

  [Types.UPDATE_AJAX_ACCESS_LEVEL_LINKS_REQUEST]: updateAjaxAccessLevelLinksRequest,
  [Types.UPDATE_AJAX_ACCESS_LEVEL_LINKS_REQUEST_SUCCESS]: updateAjaxAccessLevelLinksRequestSuccess,
  [Types.UPDATE_AJAX_ACCESS_LEVEL_LINKS_REQUEST_FAILURE]: updateAjaxAccessLevelLinksRequestFailure,

  [Types.DELETE_AJAX_ACCESS_LEVEL_LINK_REQUEST]: deleteAjaxAccessLevelLinkRequest,
  [Types.DELETE_AJAX_ACCESS_LEVEL_LINK_REQUEST_SUCCESS]: deleteAjaxAccessLevelLinkRequestSuccess,
  [Types.DELETE_AJAX_ACCESS_LEVEL_LINK_REQUEST_FAILURE]: deleteAjaxAccessLevelLinkRequestFailure,

  [Types.SET_AJAX_INCLUDED_IN_MONITORING]: setAjaxIncludedInMonitoring,
});

export const AjaxTypes = Types;
export default Creators;
